import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ChevronRight, Search, Filter, Grid, List, Download, Eye, 
  Upload, X, Check, Sparkles, Layers, Monitor, Smartphone,
  Square, RectangleHorizontal, RectangleVertical, Play,
  Clock, Star, ArrowRight, Plus, FileUp, CheckCircle
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const adSizes = [
  { id: 'all', label: 'All Sizes' },
  { id: '300x250', label: '300x250', icon: Square },
  { id: '728x90', label: '728x90', icon: RectangleHorizontal },
  { id: '160x600', label: '160x600', icon: RectangleVertical },
  { id: '300x600', label: '300x600', icon: RectangleVertical },
  { id: '320x50', label: '320x50', icon: RectangleHorizontal },
  { id: '970x250', label: '970x250', icon: RectangleHorizontal },
];

const categories = [
  { id: 'all', label: 'All Categories' },
  { id: 'retail', label: 'Retail & E-commerce' },
  { id: 'automotive', label: 'Automotive' },
  { id: 'travel', label: 'Travel & Tourism' },
  { id: 'finance', label: 'Financial Services' },
  { id: 'tech', label: 'Technology' },
  { id: 'entertainment', label: 'Entertainment' },
  { id: 'fashion', label: 'Fashion & Beauty' },
  { id: 'food', label: 'Food & Beverage' },
];

const templates: {
  id: number;
  name: string;
  category: string;
  sizes: string[];
  features: string[];
  preview: string;
  downloads: number;
  rating: number;
  isNew: boolean;
  isPremium: boolean;
}[] = [];

const Templates: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSize, setSelectedSize] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.features.some(f => f.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesSize = selectedSize === 'all' || template.sizes.includes(selectedSize);
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesSize && matchesCategory;
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      // Simulate upload progress
      setUploadProgress(0);
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setUploadComplete(true);
            return 100;
          }
          return prev + 10;
        });
      }, 200);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setUploadedFile(file);
      setUploadProgress(0);
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setUploadComplete(true);
            return 100;
          }
          return prev + 10;
        });
      }, 200);
    }
  };

  const handleUseTemplate = (templateId: number) => {
    navigate('/dashboard/create', { state: { templateId } });
  };

  const resetUpload = () => {
    setUploadedFile(null);
    setUploadProgress(0);
    setUploadComplete(false);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#635bff]/20 via-transparent to-[#00d4ff]/10" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#635bff]/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#00d4ff]/15 rounded-full blur-3xl animate-float-delayed" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 mb-6">
            <Link to="/resources" className="hover:text-slate-900 dark:hover:text-white transition-colors">Resources</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 dark:text-white">Templates</span>
          </div>
          
          <div className="max-w-4xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-[#635bff] bg-[#635bff]/10 border border-[#635bff]/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              HTML5 Ad Templates
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight">
              Start with a Template
            </h1>
            <p className="mt-6 text-xl text-slate-600 dark:text-slate-400">
              Browse our collection of professionally designed HTML5 ad templates. Customize and deploy in minutes.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-y border-slate-200 dark:border-white/10 sticky top-0 bg-white/95 dark:bg-[#0a0a0f]/95 backdrop-blur-sm z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#635bff]/50 w-64"
                />
              </div>
              
              {/* Size Filter */}
              <select
                value={selectedSize}
                onChange={(e) => setSelectedSize(e.target.value)}
                className="px-4 py-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#635bff]/50"
              >
                {adSizes.map(size => (
                  <option key={size.id} value={size.id} className="bg-white dark:bg-[#1a1a2e]">
                    {size.label}
                  </option>
                ))}
              </select>
              
              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#635bff]/50"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id} className="bg-white dark:bg-[#1a1a2e]">
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600 dark:text-slate-400">{filteredTemplates.length} templates</span>
              <div className="flex bg-slate-100 dark:bg-white/5 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-[#635bff] text-white' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-[#635bff] text-white' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Templates Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {viewMode === 'grid' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className="group bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden hover:border-[#635bff]/30 transition-all"
                >
                  {/* Preview */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={template.preview}
                      alt={template.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                        <button className="flex-1 flex items-center justify-center gap-2 py-2 bg-white text-slate-900 font-medium rounded-lg hover:bg-slate-100 transition-colors">
                          <Eye className="w-4 h-4" />
                          Preview
                        </button>
                        <button 
                          onClick={() => handleUseTemplate(template.id)}
                          className="flex-1 flex items-center justify-center gap-2 py-2 bg-[#635bff] text-white font-medium rounded-lg hover:bg-[#5449e6] transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Use
                        </button>
                      </div>
                    </div>
                    
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex gap-2">
                      {template.isNew && (
                        <span className="px-2 py-1 text-xs font-medium text-white bg-green-500 rounded-full">
                          New
                        </span>
                      )}
                      {template.isPremium && (
                        <span className="px-2 py-1 text-xs font-medium text-yellow-400 bg-yellow-400/20 rounded-full">
                          Premium
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] transition-colors">
                      {template.name}
                    </h3>
                    
                    <div className="mt-2 flex flex-wrap gap-1">
                      {template.sizes.slice(0, 3).map(size => (
                        <span key={size} className="px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-white/5 rounded">
                          {size}
                        </span>
                      ))}
                      {template.sizes.length > 3 && (
                        <span className="px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-white/5 rounded">
                          +{template.sizes.length - 3}
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-3 flex items-center justify-between text-sm text-slate-600 dark:text-slate-400">
                      <span className="flex items-center gap-1">
                        <Download className="w-3.5 h-3.5" />
                        {template.downloads.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 text-yellow-400" />
                        {template.rating}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className="group flex items-center gap-6 p-4 bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-xl hover:border-[#635bff]/30 transition-all"
                >
                  <div className="w-32 h-24 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={template.preview}
                      alt={template.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] transition-colors">
                        {template.name}
                      </h3>
                      {template.isNew && (
                        <span className="px-2 py-0.5 text-xs font-medium text-white bg-green-500 rounded-full">
                          New
                        </span>
                      )}
                      {template.isPremium && (
                        <span className="px-2 py-0.5 text-xs font-medium text-yellow-400 bg-yellow-400/20 rounded-full">
                          Premium
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-2 flex flex-wrap gap-2">
                      {template.features.map(feature => (
                        <span key={feature} className="px-2 py-0.5 text-xs text-slate-700 dark:text-slate-300 bg-slate-200 dark:bg-white/10 rounded">
                          {feature}
                        </span>
                      ))}
                    </div>
                    
                    <div className="mt-2 flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                      <span>Sizes: {template.sizes.join(', ')}</span>
                      <span className="flex items-center gap-1">
                        <Download className="w-3.5 h-3.5" />
                        {template.downloads.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 text-yellow-400" />
                        {template.rating}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button className="px-4 py-2 text-slate-900 dark:text-white bg-slate-100 dark:bg-white/10 rounded-lg hover:bg-slate-200 dark:hover:bg-white/20 transition-colors">
                      Preview
                    </button>
                    <button 
                      onClick={() => handleUseTemplate(template.id)}
                      className="px-4 py-2 text-white bg-[#635bff] rounded-lg hover:bg-[#5449e6] transition-colors"
                    >
                      Use Template
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#1a1a2e] border border-slate-200 dark:border-white/10 rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-white/10">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Upload Template</h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  resetUpload();
                }}
                className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {!uploadedFile ? (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  className="border-2 border-dashed border-slate-300 dark:border-white/20 rounded-xl p-8 text-center hover:border-[#635bff]/50 transition-colors"
                >
                  <div className="w-16 h-16 bg-[#635bff]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <FileUp className="w-8 h-8 text-[#635bff]" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                    Drop your template here
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    or click to browse your files
                  </p>
                  <p className="text-sm text-slate-500 mb-4">
                    Supports .zip files containing HTML5 ad assets
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-6 py-2 bg-[#635bff] text-white font-medium rounded-lg hover:bg-[#5449e6] transition-colors"
                  >
                    Browse Files
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-white/5 rounded-xl">
                    <div className="w-12 h-12 bg-[#635bff]/20 rounded-lg flex items-center justify-center">
                      <Layers className="w-6 h-6 text-[#635bff]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">{uploadedFile.name}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    {uploadComplete && (
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    )}
                  </div>
                  
                  {!uploadComplete && (
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600 dark:text-slate-400">Uploading...</span>
                        <span className="text-slate-900 dark:text-white">{uploadProgress}%</span>
                      </div>
                      <div className="h-2 bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#635bff] to-[#00d4ff] rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {uploadComplete && (
                    <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <div>
                          <p className="font-medium text-green-400">Upload Complete!</p>
                          <p className="text-sm text-slate-600 dark:text-slate-400">Your template is ready to use in the Ad Builder.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 dark:border-white/10">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  resetUpload();
                }}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                Cancel
              </button>
              {uploadComplete && (
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    navigate('/dashboard/create', { state: { uploadedTemplate: uploadedFile?.name } });
                  }}
                  className="px-6 py-2 bg-[#635bff] text-white font-medium rounded-lg hover:bg-[#5449e6] transition-colors"
                >
                  Open in Ad Builder
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Templates;
